import React from 'react';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, AvatarGroup, AvatarGroupItem, AvatarGroupPopover, Tooltip } from '@fluentui/react-components';
import { ChevronDown16Regular, MoreVertical16Regular } from '@fluentui/react-icons';
import styles from './css/index.module.css';
import { getInitials } from '../../utils/commonUtils';
import { isActiveDuplicateJob } from '../../utils/duplicateProjectJobs';
import { RAG_AMBER, RAG_GREEN, RAG_RED } from '../../constants/ragColors';

const LastUpdatedCell = ({
    project,
    user,
    formatLastUpdated,
    calculateColorSM,
    dev,
    adequate,
    exemplary,
    irlOutstanding,
    irlCompleted,
    handleEditProject,
    handleArchiveProject,
    openDuplicateConfirm,
    duplicateJobs,
    setTile,
    deleteModalRef,
}) => {
    const isProjectAdmin =
        project?.vdr?.vdr_admin === user?.id || project?.project_admins?.some(admin => admin.user_id === user?.id);
    const isSourceDuplicating = duplicateJobs?.some(
        job => job.sourceProjectId === project?.id && isActiveDuplicateJob(job),
    );

    if (project?.isDuplicatePlaceholder) {
        return <p className={styles.primaryTableText}>Duplicating…</p>;
    }

    return (
        <div className="d-flex align-items-center gap-1 justify-content-between">
            <p className={styles.primaryTableText}>{formatLastUpdated(project?.projectprogress?.last_updated)}</p>
            <div className="d-flex align-items-center gap-1">
                <Menu positioning="below-end">
                    <MenuTrigger disableButtonEnhancement>
                        <ChevronDown16Regular color="var(--Neutral-Gray-6)" onClick={e => e.stopPropagation()} />
                    </MenuTrigger>
                    <MenuPopover>
                        <div className={styles.footerCardMenu}>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Dataroom Files</span>
                                <span style={{ color: 'black' }}>{project?.vdr?.documents_count}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Observations</span>
                                <div className="d-flex align-items-center">
                                    <div style={{ background: RAG_RED }} className={styles.rag}>
                                        {project?.projectprogress?.red_rags || 0}
                                    </div>
                                    <div style={{ background: RAG_AMBER }} className={styles.rag}>
                                        {project?.projectprogress?.amber_rags || 0}
                                    </div>
                                    <div style={{ background: RAG_GREEN }} className={styles.rag}>
                                        {project?.projectprogress?.green_rags || 0}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Maturity Index</span>
                                <div style={{ background: calculateColorSM(parseInt(project?.projectprogress?.maturity_score)) }} className={styles.rag}>
                                    {project?.projectprogress?.maturity_score}
                                </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Security Index</span>
                                <div style={{ background: calculateColorSM(parseInt(project?.projectprogress?.security_score)) }} className={styles.rag}>
                                    {project?.projectprogress?.security_score}
                                </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Security Governance</span>
                                <div style={{ background: calculateColorSM(parseInt(project?.projectprogress?.governance_score)) }} className={styles.rag}>
                                    {project?.projectprogress?.governance_score}
                                </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>IRL</span>
                                {(() => {
                                    const outstanding = isNaN(irlOutstanding) ? 0 : irlOutstanding;
                                    const completed = isNaN(irlCompleted) ? 0 : irlCompleted;
                                    const total = outstanding + completed;
                                    if (total <= 0) {
                                        return (
                                            <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                <div style={{ flex: '1 1 0', background: '#C0C6D8' }} />
                                            </div>
                                        );
                                    }
                                    const completedPct = Math.max((completed / total) * 100, 0);
                                    const remainingPct = 100 - completedPct;
                                    return (
                                        <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                            {completedPct > 0 && <div style={{ width: `${completedPct}%`, background: RAG_GREEN }} />}
                                            {remainingPct > 0 && <div style={{ width: `${remainingPct}%`, background: '#C0C6D8' }} />}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                                <span>Team</span>
                                {(() => {
                                    const d = isNaN(dev) ? 0 : dev;
                                    const a = isNaN(adequate) ? 0 : adequate;
                                    const e = isNaN(exemplary) ? 0 : exemplary;
                                    const total = d + a + e;
                                    if (total <= 0) {
                                        return (
                                            <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                                <div style={{ flex: '1 1 0', background: 'var(--Neutral-Gray-6)' }} />
                                            </div>
                                        );
                                    }
                                    const redPct = Math.max((d / total) * 100, 0);
                                    const amberPct = Math.max((a / total) * 100, 0);
                                    const greenPct = Math.max((e / total) * 100, 0);
                                    return (
                                        <div style={{ width: 80, height: 8, display: 'inline-flex' }}>
                                            {redPct > 0 && <div style={{ width: `${redPct}%`, background: 'var(--RAG-Red)' }} />}
                                            {amberPct > 0 && <div style={{ width: `${amberPct}%`, background: 'var(--RAG-Amber)' }} />}
                                            {greenPct > 0 && <div style={{ width: `${greenPct}%`, background: 'var(--RAG-Green)' }} />}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="d-flex  justify-content-between mb-3">
                                <span style={{ width: 107, textAlign: 'left', marginTop: 6 }}>Team Members</span>
                                <div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                                    <div className="d-flex align-items-center justify-content-end gap-1 flex-wrap">
                                        {project?.team_members.map(tm => (
                                            <Tooltip key={tm?.group_member_id} content={tm?.name} relationship="description" withArrow>
                                                <div className={styles.teamMember}>{getInitials(tm?.name)}</div>
                                            </Tooltip>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </MenuPopover>
                </Menu>
                <div style={{ width: 20, height: 20 }} className={styles.lastUpdatedAction}>
                    {isProjectAdmin && (
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <MoreVertical16Regular role="button" color="var(--Neutral-Gray-6)" onClick={e => e.stopPropagation()} />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleEditProject(project?.id);
                                    }}>
                                    Edit Project
                                </MenuItem>
                                <MenuItem
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleArchiveProject(project);
                                    }}>
                                    {project?.archive ? 'Unarchive' : 'Archive'}
                                </MenuItem>
                                <MenuItem
                                    disabled={isSourceDuplicating}
                                    onClick={e => {
                                        e.stopPropagation();
                                        openDuplicateConfirm?.(project);
                                    }}>
                                    Duplicate Project
                                </MenuItem>
                                <MenuItem
                                    onClick={e => {
                                        e.stopPropagation();
                                        setTile?.(project);
                                        deleteModalRef?.current?.click();
                                    }}>
                                    Delete Project
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LastUpdatedCell;
